import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SearchableSelect from '../common/SearchableSelect';
import { coaService } from '../../services/coaService';
import { transactionService } from '../../services/transactionService';
import { useClient } from '../../context/ClientContext';

const {
  FiAlertCircle,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiCalendar,
  FiCheck,
  FiDollarSign,
  FiEdit3,
  FiLoader,
  FiX
} = FiIcons;

const normalizeType = (type) =>
  String(type || '').toLowerCase();

const normalizeDate = (value) => {
  if (!value) return '';

  return String(value).slice(0, 10);
};

const getTransactionAmount = (transaction) => {
  const debit = Number(transaction?.debit) || 0;
  const credit = Number(transaction?.credit) || 0;

  if (debit > 0) {
    return {
      amount: debit,
      side: 'debit'
    };
  }

  if (credit > 0) {
    return {
      amount: credit,
      side: 'credit'
    };
  }

  return {
    amount: 0,
    side: 'debit'
  };
};

const getTransactionType = (transaction) => {
  const sourceAccount =
    transaction?.source_account ||
    transaction?.sourceAccount ||
    transaction?.source_account_data ||
    {};

  const sourceType = normalizeType(
    sourceAccount.type
  );

  const sourceSubType = normalizeType(
    sourceAccount.sub_type
  );

  const sourceName = normalizeType(
    sourceAccount.name
  );

  const debit =
    Number(transaction?.debit) || 0;

  const credit =
    Number(transaction?.credit) || 0;

  const isCreditCard =
    sourceType === 'liability' &&
    (
      sourceSubType.includes('credit card') ||
      sourceSubType.includes('creditcard') ||
      sourceName.includes('credit card') ||
      sourceName.includes('creditcard')
    );

  /*
   * Bank / cash:
   *
   * Withdrawal:
   * source asset is credited
   *
   * Deposit:
   * source asset is debited
   *
   * Credit card:
   * Withdrawal = liability is debited
   * Deposit/refund = liability is credited
   */
  if (isCreditCard) {
    if (
      debit > 0 &&
      credit === 0
    ) {
      return 'Withdrawal';
    }

    if (
      credit > 0 &&
      debit === 0
    ) {
      return 'Deposit';
    }
  }

  if (sourceType === 'asset') {
    if (
      credit > 0 &&
      debit === 0
    ) {
      return 'Withdrawal';
    }

    if (
      debit > 0 &&
      credit === 0
    ) {
      return 'Deposit';
    }
  }

  if (sourceType === 'liability') {
    if (
      debit > 0 &&
      credit === 0
    ) {
      return 'Withdrawal';
    }

    if (
      credit > 0 &&
      debit === 0
    ) {
      return 'Deposit';
    }
  }

  return credit > debit
    ? 'Withdrawal'
    : 'Deposit';
};

const getTransactionId = (transaction) =>
  transaction?.id ||
  transaction?.originalTx?.id ||
  null;

const emptyForm = (
  type = 'Withdrawal'
) => ({
  type,
  date: new Date()
    .toISOString()
    .slice(0, 10),
  source_account_id: '',
  description: '',
  amount: '',
  category_id: '',
  notes: ''
});

export default function TransactionModal({
  isOpen,
  onClose,
  onSuccess,
  initialData,
  defaultType = 'Withdrawal'
}) {
  const { activeClient } = useClient();

  /*
   * This ref is passed to SearchableSelect.
   *
   * SearchableSelect will render its dropdown
   * into this modal container instead of
   * document.body.
   */
  const modalContentRef = useRef(null);

  const [formData, setFormData] =
    useState(
      emptyForm(defaultType)
    );

  const [allAccounts, setAllAccounts] =
    useState([]);

  const [isLoadingAccounts, setIsLoadingAccounts] =
    useState(false);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] =
    useState(null);

  const isEditMode =
    Boolean(initialData);

  const transactionId =
    getTransactionId(initialData);

  /*
   * ------------------------------------------------------------
   * ACCOUNT OPTIONS
   * ------------------------------------------------------------
   */

  const sourceAccountOptions =
    useMemo(() => {
      return allAccounts
        .filter((account) => {
          const type =
            normalizeType(account.type);

          return [
            'asset',
            'liability'
          ].includes(type);
        })
        .map((account) => ({
          ...account,
          label: account.code
            ? `${account.code} · ${account.name}`
            : account.name
        }));
    }, [allAccounts]);

  const categoryOptions =
    useMemo(() => {
      const isWithdrawal =
        normalizeType(
          formData.type
        ) === 'withdrawal';

      return allAccounts
        .filter((account) => {
          const type =
            normalizeType(account.type);

          if (isWithdrawal) {
            return [
              'expense',
              'expenses',
              'liability',
              'asset',
              'equity'
            ].includes(type);
          }

          return [
            'income',
            'revenue',
            'asset',
            'equity',
            'liability'
          ].includes(type);
        })
        .map((account) => ({
          ...account,
          label: account.code
            ? `${account.code} · ${account.name}`
            : account.name
        }));
    }, [
      allAccounts,
      formData.type
    ]);

  const uncategorizedIncome =
    useMemo(
      () =>
        allAccounts.find(
          (account) =>
            account.name ===
              'Uncategorized Income' &&
            [
              'income',
              'revenue'
            ].includes(
              normalizeType(
                account.type
              )
            )
        ),
      [allAccounts]
    );

  const uncategorizedExpense =
    useMemo(
      () =>
        allAccounts.find(
          (account) =>
            account.name ===
              'Uncategorized Expense' &&
            [
              'expense',
              'expenses'
            ].includes(
              normalizeType(
                account.type
              )
            )
        ),
      [allAccounts]
    );

  /*
   * ------------------------------------------------------------
   * LOAD ACCOUNTS
   * ------------------------------------------------------------
   */

  useEffect(() => {
    if (
      !isOpen ||
      !activeClient?.id
    ) {
      return;
    }

    let cancelled = false;

    const loadAccounts = async () => {
      setIsLoadingAccounts(true);
      setError(null);

      try {
        const accounts =
          await coaService.getAccounts(
            activeClient.id
          );

        if (!cancelled) {
          setAllAccounts(
            Array.isArray(accounts)
              ? accounts
              : []
          );
        }
      } catch (loadError) {
        console.error(
          'Failed to load chart of accounts:',
          loadError
        );

        if (!cancelled) {
          setError(
            'Failed to load your chart of accounts.'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAccounts(false);
        }
      }
    };

    loadAccounts();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    activeClient?.id
  ]);

  /*
   * ------------------------------------------------------------
   * POPULATE FORM
   * ------------------------------------------------------------
   *
   * IMPORTANT:
   * We use the exact transaction row supplied
   * by Transactions.jsx.
   *
   * We do NOT fetch using batch_id.
   */

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setError(null);

    if (!initialData) {
      setFormData(
        emptyForm(defaultType)
      );

      return;
    }

    const transaction =
      initialData;

    const {
      amount
    } = getTransactionAmount(
      transaction
    );

    const type =
      getTransactionType(
        transaction
      );

    setFormData({
      type,

      date:
        normalizeDate(
          transaction.date
        ),

      source_account_id:
        transaction.source_account_id ||
        transaction.source_account?.id ||
        '',

      description:
        transaction.description ||
        '',

      amount:
        amount > 0
          ? String(amount)
          : '',

      category_id:
        transaction.account_id ||
        transaction.account?.id ||
        '',

      notes:
        transaction.notes ||
        ''
    });
  }, [
    isOpen,
    initialData,
    defaultType
  ]);

  /*
   * ------------------------------------------------------------
   * TYPE SWITCH
   * ------------------------------------------------------------
   */

  const handleTypeSwitch = (
    type
  ) => {
    setFormData(
      (current) => ({
        ...current,
        type,

        category_id:
          type === 'Deposit'
            ? uncategorizedIncome?.id ||
              ''
            : uncategorizedExpense?.id ||
              ''
      })
    );
  };

  /*
   * ------------------------------------------------------------
   * FORM UPDATE
   * ------------------------------------------------------------
   */

  const updateField = (
    field,
    value
  ) => {
    setFormData(
      (current) => ({
        ...current,
        [field]: value
      })
    );
  };

  /*
   * ------------------------------------------------------------
   * SUBMIT
   * ------------------------------------------------------------
   */

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!activeClient?.id) {
      setError(
        'No active organization is selected.'
      );

      return;
    }

    if (
      isEditMode &&
      !transactionId
    ) {
      setError(
        'The transaction ID could not be found.'
      );

      return;
    }

    setError(null);

    if (!formData.date) {
      setError(
        'Please select a transaction date.'
      );

      return;
    }

    if (
      !formData.source_account_id
    ) {
      setError(
        'Please select a source account.'
      );

      return;
    }

    if (
      !formData.category_id
    ) {
      setError(
        'Please select a category account.'
      );

      return;
    }

    const amount =
      Number(formData.amount);

    if (
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      setError(
        'Please enter a valid positive amount.'
      );

      return;
    }

    /*
     * Verify both selected accounts
     * belong to the active organization.
     */
    const sourceAccount =
      allAccounts.find(
        (account) =>
          account.id ===
          formData.source_account_id
      );

    const categoryAccount =
      allAccounts.find(
        (account) =>
          account.id ===
          formData.category_id
      );

    if (!sourceAccount) {
      setError(
        'The selected source account is not available in this organization.'
      );

      return;
    }

    if (!categoryAccount) {
      setError(
        'The selected category account is not available in this organization.'
      );

      return;
    }

    setIsSubmitting(true);

    try {
      /*
       * Preserve the original debit/credit side
       * when editing.
       *
       * Example:
       *
       * Original:
       * debit = 100
       * credit = 0
       *
       * New:
       * debit = 150
       * credit = 0
       */

      const originalAmount =
        getTransactionAmount(
          initialData
        );

      const isCredit =
        isEditMode
          ? originalAmount.side ===
            'credit'
          : false;

      const payload = {
        date: formData.date,

        description:
          formData.description.trim() || null,

        source_account_id:
          formData.source_account_id,

        account_id:
          formData.category_id,

        notes:
          formData.notes.trim() || null,

        debit:
          isCredit
            ? 0
            : amount,

        credit:
          isCredit
            ? amount
            : 0
      };

      /*
       * EDIT
       *
       * Correct service signature:
       *
       * updateTransaction(
       *   transactionId,
       *   organizationId,
       *   payload
       * )
       */

      if (isEditMode) {
        await transactionService.updateTransaction(
          transactionId,
          activeClient.id,
          payload
        );
      } else {
        /*
         * Keep existing transaction creation
         * compatible with the current service.
         */
       await transactionService.createTransaction(
          {
            type: formData.type,
            date: formData.date,
            source_account_id: formData.source_account_id,
            description: formData.description.trim() || null,
            category_id: formData.category_id,
            amount,
            notes: formData.notes.trim() || null
          },
          [],
          activeClient.id
        );
      }

      onSuccess?.();

      onClose?.();
    } catch (submitError) {
      console.error(
        'Transaction modal submit error:',
        submitError
      );

      setError(
        submitError?.message ||
        'Failed to save the transaction.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /*
   * ------------------------------------------------------------
   * CLOSE
   * ------------------------------------------------------------
   */

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    setError(null);

    onClose?.();
  };

  if (
    typeof document ===
    'undefined'
  ) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6">

          {/* BACKDROP */}
          <motion.button
            type="button"
            aria-label="Close transaction modal"
            initial={{
              opacity: 0
            }}
            animate={{
              opacity: 1
            }}
            exit={{
              opacity: 0
            }}
            onClick={handleClose}
            className="
              absolute inset-0
              cursor-default
              bg-slate-950/55
              backdrop-blur-[6px]
            "
          />

          {/* MODAL */}
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.97,
              y: 20
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              scale: 0.97,
              y: 20
            }}
            transition={{
              duration: 0.2,
              ease: 'easeOut'
            }}
            className="
              relative z-10
              flex max-h-[92vh]
              w-full max-w-2xl
              flex-col
              overflow-hidden
              rounded-3xl
              border border-slate-200
              bg-white
              shadow-[0_30px_100px_rgba(15,23,42,0.25)]
            "
          >

            {/* ------------------------------------------------ */}
            {/* HEADER */}
            {/* ------------------------------------------------ */}

            <div className="
              flex shrink-0
              items-center justify-between
              border-b border-slate-100
              px-6 py-5
              sm:px-7
            ">

              <div className="
                flex min-w-0
                items-center gap-4
              ">

                <div
                  className={`
                    flex h-11 w-11
                    shrink-0
                    items-center justify-center
                    rounded-2xl
                    ${
                      formData.type ===
                      'Withdrawal'
                        ? 'bg-rose-50 text-rose-600'
                        : 'bg-emerald-50 text-emerald-600'
                    }
                  `}
                >
                  <SafeIcon
                    icon={
                      formData.type ===
                      'Withdrawal'
                        ? FiArrowUpRight
                        : FiArrowDownLeft
                    }
                    className="text-xl"
                  />
                </div>

                <div className="min-w-0">

                  <div className="
                    flex items-center gap-2
                  ">
                    <h2 className="
                      truncate
                      text-lg
                      font-bold
                      tracking-tight
                      text-slate-900
                    ">
                      {isEditMode
                        ? 'Edit Transaction'
                        : 'Record Transaction'}
                    </h2>

                    {isEditMode && (
                      <span className="
                        hidden
                        rounded-full
                        bg-blue-50
                        px-2 py-1
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-wider
                        text-blue-600
                        sm:inline-flex
                      ">
                        Editing
                      </span>
                    )}
                  </div>

                  <p className="
                    mt-1
                    text-xs
                    text-slate-400
                  ">
                    {isEditMode
                      ? 'Update the transaction details below.'
                      : 'Add a transaction to the accounting ledger.'}
                  </p>

                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="
                  ml-4
                  flex h-9 w-9
                  shrink-0
                  items-center justify-center
                  rounded-xl
                  text-slate-400
                  transition-colors
                  hover:bg-slate-100
                  hover:text-slate-700
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                <SafeIcon
                  icon={FiX}
                  className="text-lg"
                />
              </button>

            </div>

            {/* ------------------------------------------------ */}
            {/* BODY */}
            {/* ------------------------------------------------ */}

            <div
              ref={modalContentRef}
              className="
                relative
                flex-1
                overflow-y-auto
                bg-slate-50/50
                px-6 py-6
                sm:px-7
                custom-scrollbar
              "
            >

              {error && (
                <motion.div
                  initial={{
                    opacity: 0,
                    y: -5
                  }}
                  animate={{
                    opacity: 1,
                    y: 0
                  }}
                  className="
                    mb-5
                    flex items-start gap-3
                    rounded-2xl
                    border border-rose-100
                    bg-rose-50
                    px-4 py-3.5
                    text-sm
                    text-rose-700
                  "
                >
                  <SafeIcon
                    icon={FiAlertCircle}
                    className="
                      mt-0.5
                      shrink-0
                      text-base
                    "
                  />

                  <span className="font-medium">
                    {error}
                  </span>
                </motion.div>
              )}

              <form
                id="transaction-form"
                onSubmit={handleSubmit}
                className="space-y-6"
              >

                {/* ------------------------------------------ */}
                {/* TRANSACTION TYPE */}
                {/* ------------------------------------------ */}

                <div className="
                  rounded-2xl
                  border border-slate-200
                  bg-white
                  p-1.5
                  shadow-sm
                ">
                  <div className="
                    grid
                    grid-cols-2
                    gap-1.5
                  ">

                    <button
                      type="button"
                      onClick={() =>
                        handleTypeSwitch(
                          'Withdrawal'
                        )
                      }
                      disabled={isSubmitting}
                      className={`
                        flex
                        items-center
                        justify-center
                        gap-2
                        rounded-xl
                        px-4 py-3
                        text-xs
                        font-bold
                        transition-all
                        ${
                          formData.type ===
                          'Withdrawal'
                            ? 'bg-rose-50 text-rose-600 shadow-sm ring-1 ring-rose-100'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        }
                      `}
                    >
                      <SafeIcon
                        icon={
                          FiArrowUpRight
                        }
                      />

                      Money Out
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleTypeSwitch(
                          'Deposit'
                        )
                      }
                      disabled={isSubmitting}
                      className={`
                        flex
                        items-center
                        justify-center
                        gap-2
                        rounded-xl
                        px-4 py-3
                        text-xs
                        font-bold
                        transition-all
                        ${
                          formData.type ===
                          'Deposit'
                            ? 'bg-emerald-50 text-emerald-600 shadow-sm ring-1 ring-emerald-100'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        }
                      `}
                    >
                      <SafeIcon
                        icon={
                          FiArrowDownLeft
                        }
                      />

                      Money In
                    </button>

                  </div>
                </div>

                {/* ------------------------------------------ */}
                {/* MAIN DETAILS */}
                {/* ------------------------------------------ */}

                <div className="
                  rounded-2xl
                  border border-slate-200
                  bg-white
                  p-5
                  shadow-sm
                  sm:p-6
                ">

                  <div className="mb-5">

                    <h3 className="
                      text-sm
                      font-bold
                      text-slate-900
                    ">
                      Transaction Details
                    </h3>

                    <p className="
                      mt-1
                      text-xs
                      text-slate-400
                    ">
                      Core information for this ledger transaction.
                    </p>

                  </div>

                  <div className="
                    grid
                    grid-cols-1
                    gap-5
                    md:grid-cols-2
                  ">

                    {/* DATE */}

                    <ModalField
                      label="Transaction Date"
                      icon={FiCalendar}
                    >
                      <input
                        type="date"
                        required
                        value={
                          formData.date
                        }
                        onChange={(event) =>
                          updateField(
                            'date',
                            event.target.value
                          )
                        }
                        disabled={
                          isSubmitting
                        }
                        className="transaction-input"
                      />
                    </ModalField>

                    {/* AMOUNT */}

                    <ModalField
                      label="Amount"
                      icon={FiDollarSign}
                    >
                      <div className="relative">

                        <span className="
                          pointer-events-none
                          absolute left-4
                          top-1/2
                          -translate-y-1/2
                          text-sm
                          font-bold
                          text-slate-400
                        ">
                          $
                        </span>

                        <input
                          type="number"
                          required
                          min="0.01"
                          step="0.01"
                          placeholder="0.00"
                          value={
                            formData.amount
                          }
                          onChange={(event) =>
                            updateField(
                              'amount',
                              event.target.value
                            )
                          }
                          disabled={
                            isSubmitting
                          }
                          className="
                            transaction-input
                            pl-9
                            text-base
                            font-bold
                          "
                        />

                      </div>
                    </ModalField>

                    {/* SOURCE ACCOUNT */}

                    <ModalField
                      label="Source Account"
                      icon={FiDollarSign}
                    >
                      <SearchableSelect
                        value={
                          formData.source_account_id
                        }
                        onChange={(
                          value
                        ) =>
                          updateField(
                            'source_account_id',
                            value
                          )
                        }
                        options={
                          sourceAccountOptions
                        }
                        placeholder={
                          isLoadingAccounts
                            ? 'Loading accounts...'
                            : 'Select source account'
                        }
                        searchPlaceholder="
                          Search bank or cash account...
                        "
                        disabled={
                          isLoadingAccounts ||
                          isSubmitting
                        }
                        loading={
                          isLoadingAccounts
                        }

                        /*
                         * IMPORTANT:
                         *
                         * This prevents the dropdown
                         * from escaping to document.body.
                         *
                         * SearchableSelect renders the
                         * dropdown into this modal body.
                         */
                        portalTarget={
                          modalContentRef.current
                        }
                      />
                    </ModalField>

                    {/* CATEGORY */}

                    <ModalField
                      label={
                        formData.type ===
                        'Withdrawal'
                          ? 'Category / Expense'
                          : 'Category / Income'
                      }
                      icon={FiCheck}
                    >
                      <SearchableSelect
                        value={
                          formData.category_id
                        }
                        onChange={(
                          value
                        ) =>
                          updateField(
                            'category_id',
                            value
                          )
                        }
                        options={
                          categoryOptions
                        }
                        placeholder={
                          isLoadingAccounts
                            ? 'Loading accounts...'
                            : 'Select category'
                        }
                        searchPlaceholder="
                          Search accounting category...
                        "
                        disabled={
                          isLoadingAccounts ||
                          isSubmitting
                        }
                        loading={
                          isLoadingAccounts
                        }

                        /*
                         * Keep this dropdown
                         * inside the modal as well.
                         */
                        portalTarget={
                          modalContentRef.current
                        }
                      />
                    </ModalField>

                    {/* DESCRIPTION */}

                    <div className="md:col-span-2">

                      <ModalField
                        label="Description / Payee"
                        icon={FiEdit3}
                      >
                        <input
                          type="text"
                          placeholder="
                            e.g. Amazon Web Services, Client Invoice #102...
                          "
                          value={
                            formData.description
                          }
                          onChange={(event) =>
                            updateField(
                              'description',
                              event.target.value
                            )
                          }
                          disabled={
                            isSubmitting
                          }
                          className="transaction-input"
                        />
                      </ModalField>

                    </div>

                  </div>
                </div>

                {/* ------------------------------------------ */}
                {/* INTERNAL MEMO */}
                {/* ------------------------------------------ */}

                <div
                    className="
                      rounded-2xl
                      border border-slate-200
                      bg-white
                      p-5
                      shadow-sm
                      sm:p-6
                    "
                  >
                    <div className="mb-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900">
                            Internal Memo
                          </h3>

                          <p className="mt-1 text-xs text-slate-400">
                            Optional internal notes for your accounting team.
                          </p>
                        </div>

                        <span className="rounded-full bg-slate-50 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                          Private
                        </span>
                      </div>
                    </div>

                    <div className="relative">
                      <textarea
                        rows={5}
                        value={formData.notes}
                        onChange={(event) =>
                          updateField(
                            'notes',
                            event.target.value
                          )
                        }
                        disabled={isSubmitting}
                        maxLength={2000}
                        placeholder="Add private internal details..."
                        className="
                          block
                          min-h-[130px]
                          w-full
                          resize-y
                          rounded-xl
                          border
                          border-slate-200
                          bg-white
                          px-4
                          py-3.5
                          text-sm
                          leading-6
                          text-slate-700
                          shadow-sm
                          outline-none
                          transition-all
                          placeholder:text-slate-400
                          hover:border-slate-300
                          focus:border-blue-500
                          focus:ring-4
                          focus:ring-blue-500/10
                          disabled:cursor-not-allowed
                          disabled:bg-slate-50
                          disabled:opacity-60
                        "
                      />

                      <div className="pointer-events-none absolute bottom-3 left-4 right-4 flex justify-between">
                        <span className="text-[10px] text-slate-400">
                          Private note
                        </span>

                        <span className="bg-white/90 px-1 text-[10px] text-slate-400">
                          {formData.notes.length}/2000
                        </span>
                      </div>
                    </div>

                    <p className="mt-2 text-[10px] text-slate-400">
                      This memo is for your accounting team and is not part of the
                      transaction description.
                    </p>
                  </div>

              </form>
            </div>

            {/* ------------------------------------------------ */}
            {/* FOOTER */}
            {/* ------------------------------------------------ */}

            <div className="
              flex
              shrink-0
              flex-col-reverse
              gap-3
              border-t
              border-slate-100
              bg-white
              px-6 py-4
              sm:flex-row
              sm:items-center
              sm:justify-between
              sm:px-7
            ">

              <div className="
                text-[11px]
                text-slate-400
              ">
                {isEditMode
                  ? 'Changes will update this transaction immediately.'
                  : 'The transaction will be added to the ledger.'}
              </div>

              <div className="
                flex
                w-full
                gap-3
                sm:w-auto
              ">

                <button
                  type="button"
                  onClick={
                    handleClose
                  }
                  disabled={
                    isSubmitting
                  }
                  className="
                    flex-1
                    rounded-xl
                    border border-slate-200
                    bg-white
                    px-5 py-3
                    text-xs
                    font-bold
                    text-slate-600
                    transition-colors
                    hover:bg-slate-50
                    hover:text-slate-900
                    disabled:opacity-50
                    sm:flex-none
                  "
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  form="transaction-form"
                  disabled={
                    isSubmitting ||
                    isLoadingAccounts
                  }
                  className={`
                    flex
                    min-w-[150px]
                    flex-1
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    px-5 py-3
                    text-xs
                    font-bold
                    text-white
                    shadow-sm
                    transition-all
                    active:scale-[0.98]
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                    sm:flex-none
                    ${
                      formData.type ===
                      'Withdrawal'
                        ? 'bg-rose-600 hover:bg-rose-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }
                  `}
                >

                  {isSubmitting ? (
                    <>
                      <SafeIcon
                        icon={
                          FiLoader
                        }
                        className="
                          animate-spin
                          text-base
                        "
                      />

                      Saving...
                    </>
                  ) : (
                    <>
                      <SafeIcon
                        icon={
                          FiCheck
                        }
                        className="text-base"
                      />

                      {isEditMode
                        ? 'Save Changes'
                        : 'Post Transaction'}
                    </>
                  )}

                </button>

              </div>
            </div>

          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function ModalField({
  label,
  icon,
  children
}) {
  return (
    <div className="space-y-2">

      <label className="
        flex
        items-center
        gap-2
        text-[10px]
        font-bold
        uppercase
        tracking-wider
        text-slate-400
      ">
        <SafeIcon
          icon={icon}
          className="text-xs"
        />

        {label}
      </label>

      {children}

    </div>
  );
}